import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {partnersApi} from '../api';
import {PartnersFilter, PartnersResponse, CreatePartnerDTO, PartnerModel} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type PartnersState = {
  getInfinitePartners: UseInfiniteQueryResult<InfiniteData<PartnersResponse>, Error>;
  filters: PartnersFilter;
  updateFilter: (newFilter: Partial<PartnersFilter>) => void;
  createPartnerMutation: UseMutationResult<PartnerModel, Error, CreatePartnerDTO>;
  deletePartnerMutation: UseMutationResult<void, Error, string>;
};

const DEFAULT_PAGE_SIZE = 25;

export const usePartners = (initialFilter: PartnersFilter = {}): PartnersState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PartnersFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfinitePartners = useInfiniteQuery<
    PartnersResponse,
    Error,
    InfiniteData<PartnersResponse>,
    ['partners', 'infinite', Omit<PartnersFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['partners', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return partnersApi.getPartners({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<PartnersFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createPartnerMutation = useMutation({
    mutationFn: partnersApi.createPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partners', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Партнер успешно создан',
        name: 'createPartner',
        theme: 'success',
      });
    },
  });

  const deletePartnerMutation = useMutation({
    mutationFn: partnersApi.deletePartner,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partners', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Партнер успешно удален',
        name: 'deletePartner',
        theme: 'success',
      });
    },
  });

  return {
    getInfinitePartners,
    filters,
    updateFilter,
    createPartnerMutation,
    deletePartnerMutation,
  };
};


